import React, { useEffect, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { changeValueInArray } from 'utils/functions';
// import { useMessageBox } from '@totalsoft/rocket-ui/DialogBox';
import {
  LOAD_EXPORT_DOCUMENT_LIST_MUTATION,
  EXPORT_DOCUMENT_MUTATION,
  PRINT_DOCUMENTS_MUTATION,
  CANCEL_DOCUMENT_SIGN_MUTATION,
  SIGN_DOCUMENTS_MUTATION
} from '../mutations';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import {
  EXPORT_DOCUMENT_LIST_EXECUTED,
  EXPORT_DOCUMENT_EXECUTED,
  PRINT_DOCUMENTS_EXECUTED,
  DOCUMENT_PRINT_SIGNING_RESET,
  EXPORT_DOCUMENT_LIST_UPDATED,
  DOCUMENTS_READY_FOR_SEND_EMAIL
} from '../subscriptions';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import DocumentsExport from './DocumentsExport';
import { buildDMSDownloadUrl, buildDownloadUrl, buildExportDownloadUrl } from '../actionCreators';
import { useToast } from 'utils/hooks';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import { items, when, required, shape, fromModel } from '@totalsoft/pure-validations';
import { useValidation, isValid } from '@totalsoft/pure-validations-react';
import { FakeText } from '@totalsoft/rocket-ui';
import MessageBoxModal from './MessageBoxModal';
import { useSelector } from 'react-redux';
import { selectUserId } from 'selectors/userInfoSelectors';

export const documentSigningValidator = items(fromModel(model => shape({ signatureTypeId: required |> when(model?.isSelected) })));

const DocumentsExportContainer = ({ documentId, siteId, generated, forceReloadDocuments, clientDefaultEmail }) => {
  const { t } = useTranslation();
  const [signing, setSigning] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [disableDownload, setDisableDownload] = useState(false);
  const [localDocuments, setLocalDocuments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  const { allowIndividualSigningOfDocuments } = useContext(ApplicationFeaturesContext);
  const [validation, validate] = useValidation(documentSigningValidator);
  const modelIsValid = validation |> isValid;
  const addToast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [newDocumentPrintId, setNewDocumentPrintId] = useState(null);
  const [showModalMessageBox, setShowModalMessageBox] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const userId = useSelector(state => selectUserId(state));
  const { storeKey: docStoreKey } = useServerErrorHandling({
    effect: _ => {
      setDocumentsLoaded(false);
    }
  });

  const { storeKey: signStoreKey } = useServerErrorHandling({
    effect: _ => {
      setSigning(false);
    }
  });

  const { storeKey: printStoreKey } = useServerErrorHandling({
    effect: _ => {
      setPrinting(false);
    }
  });

  const { storeKey: sendEmailStoreKey } = useServerErrorHandling({
    effect: _ => {
      setSendingEmail(false);
    }
  });

  const [loadExportDocumentList] = useMutation(LOAD_EXPORT_DOCUMENT_LIST_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setDocumentsLoaded(false);
    },
    onCompleted: data => {
      const { correlationId } = data.getExportDocumentList;
      docStoreKey({ correlationId });
    }
  });

  const [exportDocument] = useMutation(EXPORT_DOCUMENT_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setDocumentsLoaded(false);
    },
    onCompleted: data => {
      const { correlationId } = data.exportDocument;
      docStoreKey({ correlationId });
    }
  });

  const [printDocuments] = useMutation(PRINT_DOCUMENTS_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setPrinting(false);
    },
    onCompleted: data => {
      const { correlationId } = data.printDocuments;
      printStoreKey({ correlationId });
      setPrinting(true);
    }
  });

  const [signDocuments] = useMutation(SIGN_DOCUMENTS_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setSigning(false);
    },
    onCompleted: data => {
      const { correlationId } = data.signDocuments;
      signStoreKey({ correlationId });
      setSigning(true);
    }
  });

  const [cancelDocumentSign] = useMutation(CANCEL_DOCUMENT_SIGN_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setSigning(false);
    },
    onCompleted: data => {
      const { correlationId } = data.cancelDocumentSign;
      signStoreKey({ correlationId });
    }
  });

  useEffect(() => {
    if (forceReloadDocuments || (!documentsLoaded && generated === true)) {
      setDisableDownload(true);
      loadExportDocumentList({
        variables: {
          input: {
            documentId,
            siteId
          }
        }
      });
      setDisableDownload(false);
    }
  }, [loadExportDocumentList, documentId, siteId, documentsLoaded, generated, forceReloadDocuments]);

  useEffect(() => {
    if (documentsLoaded) {
      setLocalDocuments(documents.map(doc => ({ ...doc, isSelected: false })));
    }
  }, [documentsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (documentsLoaded) {
      setLocalDocuments(documents.map(doc => ({ ...doc, isSelected: false })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  useEffect(() => {
    validate(localDocuments);
  }, [localDocuments, validate]);

  const downloadFile = () => fileId => {
    async function downloadFileAsync() {
      try {
        let link = buildDMSDownloadUrl(fileId)();
        window.location = link;
      } catch (error) {
        // continue regardless of error
      }
    }
    downloadFileAsync();
  };

  const onDocumentPrintPropertyChange = useCallback(
    (item, propName) => event => {
      const changedLocalDocuments = changeValueInArray(localDocuments, 'documentPrintId', item.documentPrintId, propName, event);
      setLocalDocuments(changedLocalDocuments);
    },
    [localDocuments]
  );

  const onExportDocumentAndDownload = useCallback(
    documentPrintId => {
      async function onExportDocumentAndDownloadAsync() {
        exportDocument({
          variables: {
            documentId,
            siteId,
            documentPrintId
          }
        });
      }
      onExportDocumentAndDownloadAsync();
    },
    [documentId, exportDocument, siteId]
  );

  const signatureTypesAreEqual = useCallback(localDocuments => {
    if (localDocuments.filter(x => x.isSelected).length === 0) return true;
    const docs = localDocuments.filter(x => x.isSelected && x.signatureTypeId !== null);
    const signatureTypeIds = docs.map(a => a.signatureTypeId);
    return new Set(signatureTypeIds).size === 1;
  }, []);

  const onSign = useCallback(() => {
    async function onSignAsync() {
      setSigning(true);
      let docs;
      let docsForSign;
      if (documentsLoaded) {
        if (allowIndividualSigningOfDocuments) {
          docs = localDocuments.filter(x => x.isSelected);
          docsForSign = docs.map(a => {
            return { FileId: a.fileId, DocumentPrintId: a.documentPrintId, SignatureTypeId: a.signatureTypeId };
          });
        } else {
          docs = localDocuments.filter(x => x.needsSigning);
          docsForSign = docs.map(a => {
            return { FileId: a.fileId, DocumentPrintId: a.documentPrintId, SignatureTypeId: a.signatureTypeId };
          });
          const updatedLocalDocuments = localDocuments.map(doc => ({
            ...doc,
            isSelected: doc.needsSigning ? true : false
          }));

          setLocalDocuments(updatedLocalDocuments);
          if (!signatureTypesAreEqual(updatedLocalDocuments)) {
            setSigning(false);
            return;
          }
        }
      }
      validate(localDocuments);

      if (docsForSign.length === 0) {
        setSigning(false);
        return;
      }

      try {
        signDocuments({
          variables: {
            documentId,
            siteId,
            files: docsForSign
          }
        });
      } catch (error) {
        // continue regardless of error
      }
    }
    onSignAsync();
  }, [
    allowIndividualSigningOfDocuments,
    documentId,
    documentsLoaded,
    localDocuments,
    signDocuments,
    siteId,
    signatureTypesAreEqual,
    validate
  ]);

  const onOpenModalMessageBox = useCallback(() => {
    setShowModalMessageBox(true);
  }, []);

  const onCloseModalMessageBox = useCallback(() => {
    setShowModalMessageBox(false);
  }, []);

  const onConfirmationMessageBox = useCallback(() => {
    onCloseModalMessageBox();

    if (!newDocumentPrintId) return;
    try {
      cancelDocumentSign({
        variables: {
          documentId,
          siteId,
          documentPrintId: newDocumentPrintId
        }
      });
    } catch (error) {
      // continue regardless of error
    }
    setNewDocumentPrintId(null);
  }, [cancelDocumentSign, documentId, newDocumentPrintId, onCloseModalMessageBox, siteId]);

  const handleCancelSign = useCallback(
    documentPrintId => {
      async function cancelSignAsync() {
        setNewDocumentPrintId(documentPrintId);
        onOpenModalMessageBox();
      }
      cancelSignAsync();
    },
    [onOpenModalMessageBox]
  );

  const [sendEmail] = useMutation(PRINT_DOCUMENTS_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
      setSendingEmail(false);
    },
    onCompleted: data => {
      const { correlationId } = data.printDocuments;
      sendEmailStoreKey({ correlationId });
    }
  });

  const onPrintAll = useCallback(() => {
    async function onPrintAllAsync() {
      setPrinting(true);
      try {
        let docs = localDocuments.filter(x => x.isSelected);
        let docsForPrint = docs.map(a => {
          return { DocumentPrintId: a.documentPrintId, SignatureTypeId: a.signatureTypeId };
        });
        if (docsForPrint.length === 0) {
          setPrinting(false);
          return;
        }
        printDocuments({
          variables: {
            documentId,
            siteId,
            selectedDocuments: docsForPrint,
            sendEmail: false
          }
        });
      } catch (error) {
        // continue regardless of error
      }
    }
    onPrintAllAsync();
  }, [documentId, localDocuments, printDocuments, siteId]);

  const handleCloseEmailModal = () => {
    setShowModal(false);
  };

  const handleOpenEmailModal = () => {
    setShowModal(true);
  };

  const handleSendDocumentsByEmail = useCallback(() => {
    try {
      setSendingEmail(true);

      let docs = localDocuments.filter(x => x.isSelected);
      let docsForPrint = docs.map(a => {
        return { DocumentPrintId: a.documentPrintId, SignatureTypeId: a.signatureTypeId };
      });
      if (docsForPrint.length === 0 || !clientDefaultEmail) {
        setSendingEmail(false);
        return;
      }
      sendEmail({
        variables: {
          documentId,
          siteId,
          selectedDocuments: docsForPrint,
          sendEmail: true,
          defaultEmail: clientDefaultEmail
        }
      });
      setShowModal(false);
    } catch (error) {
      // continue regardless of error
    }
  }, [clientDefaultEmail, documentId, localDocuments, sendEmail, siteId]);

  useGlobalSubscription(EXPORT_DOCUMENT_LIST_EXECUTED, {
    variables: {
      documentId,
      siteId,
      userId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      setDocuments(data.exportDocumentListExecuted.list);
      setDocumentsLoaded(true);
    }
  });

  useGlobalSubscription(EXPORT_DOCUMENT_EXECUTED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const url = buildExportDownloadUrl(data.exportDocumentExecuted.filePath, data.exportDocumentExecuted.fileName)();
      window.location = url;
    }
  });

  useGlobalSubscription(PRINT_DOCUMENTS_EXECUTED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const url = buildDownloadUrl(
        documentId,
        siteId,
        data.printDocumentsExecuted.file.filePath,
        data.printDocumentsExecuted.file.fileName
      )();
      window.location = url;
      setPrinting(false);
    }
  });

  useGlobalSubscription(DOCUMENTS_READY_FOR_SEND_EMAIL, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: () => {
      setSendingEmail(false);
    }
  });

  useGlobalSubscription(DOCUMENT_PRINT_SIGNING_RESET, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: () => {
      setSigning(false);
    }
  });

  useGlobalSubscription(EXPORT_DOCUMENT_LIST_UPDATED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const updatedDocs = documents.map(doc => {
        const updatedDoc = data.exportDocumentListUpdated.documentPrintList.find(item => item.documentPrintId === doc.documentPrintId);

        return updatedDoc
          ? {
              ...doc,
              signingStatus: updatedDoc.signingStatus,
              fileId: updatedDoc.fileId,
              exportedBy: updatedDoc.exportedBy,
              exportedDate: updatedDoc.exportedDate,
              isUploaded: updatedDoc.isUploaded,
              needsSigning: updatedDoc.needsSigning,
              isSigningInProgress: updatedDoc.isSigningInProgress
            }
          : doc;
      });
      setDocuments(updatedDocs);
      //setDocumentsLoaded(false);
    }
  });

  const handleSelectAllDocuments = useCallback(
    event => {
      const localDocumentsSelected = localDocuments.map(localDocument => {
        return localDocument.isSigningInProgress ? localDocument : { ...localDocument, isSelected: event.target.checked };
      });
      setLocalDocuments(localDocumentsSelected);
    },
    [localDocuments]
  );

  return (
    <>
      {!documentsLoaded ? (
        <FakeText lines={6} />
      ) : (
        <>
          <DocumentsExport
            documents={localDocuments}
            downloadFile={downloadFile()}
            exportDocumentAndDownload={onExportDocumentAndDownload}
            signing={signing}
            isAnyDocumentChecked={localDocuments.some(document =>
              allowIndividualSigningOfDocuments ? document.needsSigning && document.isSelected : document.needsSigning
            )}
            onSign={onSign}
            onCancelSign={handleCancelSign}
            disableDownload={disableDownload}
            printing={printing}
            onPrintAll={onPrintAll}
            allowIndividualSigningOfDocuments={allowIndividualSigningOfDocuments}
            validation={validation}
            modelIsValid={modelIsValid}
            onDocumentPrintPropertyChange={onDocumentPrintPropertyChange}
            onSelectAllDocuments={handleSelectAllDocuments}
            sendDocumentsByEmail={handleSendDocumentsByEmail}
            sendingEmail={sendingEmail}
            closeEmailModal={handleCloseEmailModal}
            showModal={showModal}
            openEmailModal={handleOpenEmailModal}
            defaultEmail={clientDefaultEmail}
            signatureTypesAreEqual={signatureTypesAreEqual(localDocuments)}
          />
          {showModalMessageBox && (
            <MessageBoxModal
              open={showModalMessageBox}
              onConfirmation={onConfirmationMessageBox}
              onCloseModal={onCloseModalMessageBox}
              title={t('OfferEdit.Documents.CancelSigning.Title')}
              text={t('OfferEdit.Documents.CancelSigning.Content')}
            />
          )}
        </>
      )}
    </>
  );
};

DocumentsExportContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  generated: PropTypes.bool.isRequired,
  forceReloadDocuments: PropTypes.bool.isRequired,
  clientDefaultEmail: PropTypes.string
};

export default DocumentsExportContainer;
